import { Button, Input } from "antd";
import { useContext, useEffect, useState } from "react";
import { useSendEmailForVerifyMutation } from "./register.service";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { CreateAccountContext } from "../../provider/createAccountProvider";
import {
  ADDITIONAL_LOCATION_PRICE,
  BASE_SUBSCRIPTION_PRICE,
  PRINTER_PRICE,
  formatGbp,
} from "../../constant/pricing";

const FEATURES = [
  "Unlimited staff",
  "Digital SFBB diary",
  "HACCP records",
  "Temperature monitoring",
  "Cleaning schedules",
  "Opening & closing checks",
  "Probe calibration",
  "Allergen management",
  "Food date labeling",
  "1 business/location included",
];

export default function EmailInputPage() {
  const navigate = useNavigate();
  const { setVerifyEmail } = useContext(CreateAccountContext);

  const query = new URLSearchParams(useLocation().search);
  const sessionId = query.get("sessionId");
  const success = query.get("success");

  useEffect(() => {
    if (success === "true" && sessionId) {
      navigate(`/create-restaurant?sessionId=${sessionId}`);
    }
  }, [sessionId, success, navigate]);

  const [email, setEmail] = useState("");
  const [error, setError] = useState("");

  const [
    requestSendEmailForVerify,
    { isSuccess, isLoading, data: sentData = { data: "", sessionId: "" } },
  ] = useSendEmailForVerifyMutation();
  const { data } = sentData;

  const validateEmail = (value) => {
    if (!value || value.trim() === "") {
      return "Please enter your email address";
    }
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(value)) {
      return "Please enter a valid email address";
    }
    return "";
  };

  const handleSendEmailForVerify = () => {
    const validationError = validateEmail(email);
    if (validationError) {
      setError(validationError);
      toast.error(validationError);
      return;
    }

    setError("");
    requestSendEmailForVerify({ email });
  };

  useEffect(() => {
    if (!isSuccess) return;

    setVerifyEmail(email);
    try {
      sessionStorage.setItem("sfbb_verify_email", email);
    } catch (_) {
      /* ignore */
    }

    if (data === "complete_registration" && sentData?.sessionId) {
      navigate(`/create-restaurant?sessionId=${sentData.sessionId}`);
      return;
    }

    if (data === "payment") {
      navigate("/make-payment");
      return;
    }

    toast.success(
      "Verification email sent. Please use the link in your inbox to proceed."
    );
    navigate("/email-verifycation", {
      state: { email, emailAlreadySent: true },
    });
  }, [isSuccess, data, email, navigate, setVerifyEmail]);

  return (
    <div className="register-signincontainer signup-page">
      <div className="saas-card">
        <div className="signup-product-card" aria-label="SFBB Pro App pricing">
          <div className="signup-hero-widget">
            <div className="signup-hero-logo-clip">
              <img
                className="signup-hero-logo-img"
                src="/mark1.jpg"
                alt="SFBB Pro"
              />
            </div>
            <div className="signup-hero-trial">14 days free</div>
            <div className="signup-hero-price">
              Then {formatGbp(BASE_SUBSCRIPTION_PRICE)} per month
            </div>
            <span className="signup-hero-spark" aria-hidden="true">
              ✦
            </span>
          </div>
        </div>

        <div className="signup-plan-summary">
          SFBB Pro {formatGbp(BASE_SUBSCRIPTION_PRICE)}/mo · 1 location included
        </div>

        <div className="signup-after-reg" role="note">
          <strong>After registration</strong> you can add extra locations (+
          {formatGbp(ADDITIONAL_LOCATION_PRICE)}/mo each) and order a label
          printer ({formatGbp(PRINTER_PRICE)}) from My Account.
        </div>

        <div className="signup-email-block">
          <Input
            value={email}
            placeholder="Enter your email address"
            onChange={(e) => {
              setEmail(e.target.value);
              if (error) setError("");
            }}
            onPressEnter={handleSendEmailForVerify}
            status={error ? "error" : ""}
            className="signup-email-input"
            size="large"
          />
          {error && <div className="signup-error">{error}</div>}
          <Button
            loading={isLoading}
            onClick={handleSendEmailForVerify}
            disabled={isLoading}
            className="signup-cta-btn"
            size="large"
            block
          >
            GET STARTED →
          </Button>
        </div>

        <div className="email-input-form-static">
          14-day free trial · Secure payment · Cancel anytime
        </div>

        <div className="feature-grid">
          {FEATURES.map((feature) => (
            <div className="feature-item" key={feature}>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M5 13l4 4L19 7" stroke="currentColor" />
              </svg>
              <span>{feature}</span>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        .signup-page.register-signincontainer {
          background-color: rgb(0, 111, 81);
          color: white;
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 100vh;
          padding: 1.25rem 1rem;
          box-sizing: border-box;
        }

        .signup-page .saas-card {
          max-width: 560px;
          width: 100%;
          background: rgba(255, 255, 255, 0.06);
          backdrop-filter: blur(4px);
          border-radius: 28px;
          padding: 1.35rem 1.5rem 1.25rem;
          box-shadow: 0 25px 50px -10px rgba(0, 0, 0, 0.35);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .signup-product-card {
          display: flex;
          justify-content: center;
          margin-bottom: 0.65rem;
        }

        .signup-hero-widget {
          position: relative;
          box-sizing: border-box;
          width: 220px;
          border-radius: 14px;
          background: #91ba41;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding: 0 0 12px;
          color: #1a1a1a;
          overflow: hidden;
          font-family: Arial, Helvetica, sans-serif;
        }

        .signup-hero-logo-clip {
          width: 100%;
          overflow: hidden;
          line-height: 0;
          aspect-ratio: 833 / 365;
          margin-bottom: -2px;
        }

        .signup-hero-logo-img {
          display: block;
          width: 100%;
          height: auto;
          margin: 0;
          vertical-align: top;
          user-select: none;
          pointer-events: none;
        }

        .signup-hero-trial {
          font-size: 22px;
          font-weight: 800;
          line-height: 1.05;
          letter-spacing: -0.02em;
          color: #111111;
          padding: 10px 10px 0;
          width: 100%;
          box-sizing: border-box;
        }

        .signup-hero-price {
          margin-top: 3px;
          font-size: 12px;
          font-weight: 500;
          color: #1a1a1a;
          padding: 0 10px 2px;
          width: 100%;
          box-sizing: border-box;
        }

        .signup-hero-spark {
          position: absolute;
          right: 8px;
          bottom: 6px;
          font-size: 9px;
          color: rgba(255, 255, 255, 0.7);
          line-height: 1;
        }

        .signup-page .feature-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.55rem 1rem;
          margin: 0.9rem 0 0 0;
          background: rgba(255, 255, 255, 0.04);
          padding: 0.9rem 1rem;
          border-radius: 18px;
          border: 1px solid rgba(255, 255, 255, 0.08);
        }

        .signup-plan-summary {
          text-align: center;
          font-size: 0.95rem;
          font-weight: 600;
          color: #f9d77e;
          margin-bottom: 0.9rem;
        }

        .signup-page .feature-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.88rem;
          color: #f0faf6;
          line-height: 1.3;
        }

        .signup-page .feature-item svg {
          flex-shrink: 0;
          width: 16px;
          height: 16px;
          fill: none;
          stroke: #b4e6d0;
          stroke-width: 2.5;
          stroke-linecap: round;
          stroke-linejoin: round;
        }

        .signup-after-reg {
          text-align: center;
          background: rgba(255, 255, 255, 0.08);
          border: 1px solid rgba(255, 255, 255, 0.12);
          border-radius: 14px;
          padding: 0.7rem 0.9rem;
          margin-bottom: 0.9rem;
          color: #e8f6f0;
          font-size: 0.84rem;
          line-height: 1.45;
        }

        .signup-after-reg strong {
          color: #fff;
        }

        .signup-email-input.ant-input {
          border-radius: 999px !important;
          margin-bottom: 0.7rem;
          padding: 0.65rem 1rem;
          font-size: 0.95rem;
        }

        .signup-cta-btn.ant-btn {
          height: auto !important;
          min-height: 48px;
          border: none !important;
          border-radius: 999px !important;
          background: #f9d77e !important;
          color: #1e3a2a !important;
          font-weight: 700 !important;
          font-size: 1rem !important;
          letter-spacing: 0.5px;
        }

        .signup-cta-btn.ant-btn:hover:not(:disabled) {
          background: #f5cc62 !important;
          color: #1e3a2a !important;
        }

        .signup-error {
          color: #ffb3b3;
          font-size: 12px;
          margin: -0.3rem 0 0.5rem 0.75rem;
        }

        .signup-page .email-input-form-static {
          background: rgba(255, 255, 255, 0.05);
          text-align: center;
          padding: 10px 8px;
          margin-top: 0.85rem;
          border-radius: 999px;
          border: 1px solid rgba(255, 255, 255, 0.06);
          color: #d8efe6;
          font-size: 0.8rem;
        }

        @media (max-width: 520px) {
          .signup-page .feature-grid {
            grid-template-columns: 1fr;
          }

          .signup-page .saas-card {
            padding: 1.15rem 1rem 1.1rem;
          }
        }
      `}</style>
    </div>
  );
}
